<template>
  <div class="flex gap-1 pt-1 w-full">
    <div class="flex grow gap-1 w-full">
      <div class="grow flex flex-col w-full">
        <div
          class="border py-2 px-3 rounded-md text-style-sm font-medium w-full"
          :class="{
            'bg-brand-primary_alt border-brand text-brand-secondary':
              (type === 'space' || level !== 1) && isSelected(node.id),
            'text-secondary bg-primary':
              !isSelected(node.id) || (type !== 'space' && level === 1),
          }"
        >
          <div
            class="flex items-center justify-between cursor-pointer w-full space-x-2"
            @click="toggleChildren"
          >
            <div class="flex items-center space-x-2 truncate">
              <div v-if="hasChildren" class="button-tertiary-gray-fg">
                <Icon name="chevron-down" :class="{ 'rotate-180': isOpen }" />
              </div>
              <div v-if="level === 1">
                <Indicator
                  size="xs"
                  name="building"
                  :mode="
                    (type === 'space' || level !== 1) && isSelected(node.id)
                      ? 'solid'
                      : 'transparent'
                  "
                />
              </div>
              <Tooltip
                :title="breadcrumb"
                :delay="300"
                side="bottom"
                :disableClosingTrigger="true"
              >
                <div class="truncate">
                  <span v-for="(part, index) in highlightedParts" :key="index">
                    <mark v-if="part.highlight" class="bg-yellow-300">{{
                      part.text
                    }}</mark>
                    <template v-else>{{ part.text }}</template>
                  </span>
                </div>
              </Tooltip>
            </div>
            <div class="flex items-center space-x-2">
              <Id
                size="md"
                :class="{
                  '!bg-brand-primary border-transparent !text-brand-secondary [&>span]:!text-brand-secondary':
                    (type === 'space' || level !== 1) && isSelected(node.id),
                }"
                v-if="node.identifier"
                :label="node.identifier"
                class="!max-w-24"
              />
              <State
                v-if="
                  type === 'space' ||
                  (level !== 1 && !node.id.includes('ungrouped-'))
                "
                :state="state"
                :has-children="hasChildren"
                @click.stop="
                  emit('update:filters', { node: node, state: state })
                "
              />
            </div>
          </div>
        </div>
        <div
          v-if="isOpen && node.children && node.children.length"
          class="flex flex-col pl-2 relative"
        >
          <div
            v-for="(child, index) in node.children"
            :key="child.id"
            class="relative"
          >
            <div
              class="absolute -top-0 left-0 w-4 border-l border-primary"
              :class="index === node.children.length - 1 ? 'h-5' : 'h-full'"
            ></div>
            <div class="flex items-center relative">
              <div
                class="w-2 h-2 border-l border-b rounded-bl-xs border-primary absolute top-[18px] left-0"
              ></div>
              <div class="flex-grow pl-2 w-full">
                <TreeNode
                  :node="child"
                  :level="level + 1"
                  :selectedNodeIds="selectedNodeIds"
                  :shouldAutoOpen="shouldAutoOpen"
                  :searchTerm="searchTerm"
                  :breadcrumb="getFullPath(child)"
                  :type="type"
                  @update:filters="emit('update:filters', $event)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Vue Composition API
import { ref, computed, watch } from "vue";

// Component imports
import State from "./State.vue";
import { Id } from "@/SharedComponents/Badges";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { Indicator } from "@/SharedComponents/Indicator";
import { Icon } from "@/SharedComponents/Icon";

// Import composables
import { States } from "@/Composables/useSpacesAndAssetsTree";

// Define props
const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  breadcrumb: {
    type: String,
    required: true,
  },
  searchTerm: {
    type: String,
    required: true,
  },
  selectedNodeIds: {
    type: Array,
    required: true,
  },
  shouldAutoOpen: {
    type: Boolean,
    default: false,
  },
  level: {
    type: Number,
    required: true,
  },
});

/**
 * Emits an event to update filters
 * @type {function}
 */
const emit = defineEmits(["update:filters"]);

/**
 * Reactive reference to control the open/closed state of the node
 * @type {import('vue').Ref<boolean>}
 */
const isOpen = ref(false);

/**
 * Computed property to check if the node has children
 * @type {import('vue').ComputedRef<boolean>}
 */
const hasChildren = computed(
  () => props.node.children && props.node.children.length > 0
);

/**
 * Generates the full path for a child node
 * @param {Object} child - The child node object
 * @returns {string} The full path including the current breadcrumb and child name
 */
const getFullPath = (child) => {
  return props.breadcrumb + " > " + child.name;
};

/**
 * Watches for changes in the shouldAutoOpen prop
 * Opens the node if shouldAutoOpen becomes true
 */
watch(
  () => props.shouldAutoOpen,
  (newVal) => {
    if (newVal) {
      isOpen.value = true;
    }
  },
  { immediate: true }
);

/**
 * Checks if a node is selected based on its ID
 * @param {string} id - The ID of the node to check
 * @returns {boolean} True if the node is selected, false otherwise
 */
const isSelected = (id) => {
  return (
    props.selectedNodeIds.buildings?.includes(id) ||
    props.selectedNodeIds.children.includes(id)
  );
};

/**
 * Toggles the open/closed state of the node's children
 */
const toggleChildren = () => {
  isOpen.value = !isOpen.value;
};

/**
 * Recursively checks if all children of a node are selected
 * @param {Object} node - The node to check
 * @returns {boolean} True if all children are selected, false otherwise
 */
const checkChildrenSelected = (node) => {
  if (!node.children || node.children.length === 0) return true;

  return node.children.every((child) => {
    const areChildrenSelected = checkChildrenSelected(child);
    return isSelected(child.id) && areChildrenSelected;
  });
};

/**
 * Computed property that checks if all children of the current node are selected
 * @type {ComputedRef<boolean>}
 */
const allChildrenSelected = computed(() => {
  return checkChildrenSelected(props.node);
});

/**
 * Computed property that checks if all direct children of the current node are selected
 * @type {ComputedRef<boolean>}
 */
const allDirectChildrenSelected = computed(() => {
  if (!props.node.children || props.node.children.length === 0) return true;

  return props.node.children.every((child) => {
    return isSelected(child.id);
  });
});

/**
 * Computed property that checks if some direct children of the current node are selected
 * @type {ComputedRef<boolean>}
 */
const someDirectChildrenSelected = computed(() => {
  if (!props.node.children || props.node.children.length === 0) return true;

  return props.node.children.some((child) => {
    return isSelected(child.id);
  });
});

/**
 * Computed property that determines the state of the current node
 * @type {ComputedRef<string>}
 */
const state = computed(() => {
  const checkChildrenSelected = (node) => {
    if (!node.children || node.children.length === 0) return false;
    return node.children.some((child) => {
      return isSelected(child.id) || checkChildrenSelected(child);
    });
  };

  if (!isSelected(props.node.id) && checkChildrenSelected(props.node)) {
    return States.INTERMEDIATE;
  }

  if (isSelected(props.node.id) && !allChildrenSelected.value) {
    return States.SINGLE;
  }

  if (isSelected(props.node.id) && allChildrenSelected.value) {
    return States.ALL;
  }

  if (
    !isSelected(props.node.id) &&
    !allDirectChildrenSelected.value &&
    someDirectChildrenSelected.value
  ) {
    return States.INTERMEDIATE;
  }

  if (
    !isSelected(props.node.id) &&
    !allChildrenSelected.value &&
    allDirectChildrenSelected.value &&
    someDirectChildrenSelected.value
  ) {
    return States.INTERMEDIATE;
  }

  return States.EMPTY;
});

/**
 * Computed property that highlights parts of the node name based on the search term
 * @type {ComputedRef<Array<{text: string, highlight: boolean}>>}
 */
const highlightedParts = computed(() => {
  if (!props.searchTerm) return [{ text: props.node.name, highlight: false }];

  const parts = [];
  const lowerName = props.node.name.toLowerCase();
  const lowerQuery = props.searchTerm.toLowerCase();
  let lastIndex = 0;
  let index;

  while ((index = lowerName.indexOf(lowerQuery, lastIndex)) !== -1) {
    if (index > lastIndex) {
      parts.push({
        text: props.node.name.slice(lastIndex, index),
        highlight: false,
      });
    }
    parts.push({
      text: props.node.name.slice(index, index + props.searchTerm.length),
      highlight: true,
    });
    lastIndex = index + props.searchTerm.length;
  }

  if (lastIndex < props.node.name.length) {
    parts.push({ text: props.node.name.slice(lastIndex), highlight: false });
  }

  return parts;
});
</script>
